import { Component, DestroyRef, input, OnInit, signal, WritableSignal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  finalize,
  of,
  take,
  switchMap,
  map,
  concatMap,
  forkJoin,
  catchError,
  throwError,
  tap,
  Observable,
} from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { formatFromDateWithSeconds } from 'src/app/common/datetime_utility';
import { findingAttackToDisplayName, isTaintAnalysisRule } from 'src/app/common/findingRulesUtils';
import { parseAdditionalData } from 'src/app/common/stringUtils';
import {
  anyOwaspToDisplayName,
  getviolationRuleRiskReasonCardFieldKey,
  getViolationRuleToOwaspLinks,
} from 'src/app/common/violationRulesUtils';
import { ISecurityTag } from 'src/app/components/security-tag/security-tag.component';
import { ISidepanelTab } from 'src/app/components/sidepanel-tabs/sidepanel-tabs.component';
import { ITimelineItem } from 'src/app/components/timeline/timeline.component';
import {
  LabelsDialogComponent,
  LabelsDialogInputData,
  LabelsDialogOutputData,
} from 'src/app/components/labels-dialog/labels-dialog.component';
import { IntegrationModel } from 'src/app/models/api';
import { AgentStepModel, AiService, StepType } from 'src/app/models/api/agentStep.model';
import { Evidence, FindingModel } from 'src/app/models/api/finding.model';
import { LabelBindingModel, LabelModel } from 'src/app/models/api/common';
import { AiFindingsTableRowViewModel } from 'src/app/models/viewModels/aiFindingsTableRowViewModel';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import {
  ActionGetAgentStepSensitiveDataResponseModel,
  AiAgentStepsHttpService,
  SensitiveDataRequestActions,
} from 'src/app/services/httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service';
import { AiFindingsHttpService } from 'src/app/services/httpServices/detection/aiFindingsHttpService/ai-findings-http.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { SidePanelService } from '../../../features/side-panel/services/side-panel.service';
import { Router } from '@angular/router';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { ICardField } from 'src/app/models/viewModels/cardModel';
import { TemplateRef, viewChild } from '@angular/core';
import {
  getAiServiceDisplayName,
  getAiServiceLogoPath,
  getClientApplicationIconPath,
  isM365CustomAgent,
} from 'src/app/common/aiServiceUtils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { AgentInventoryNavigationService } from 'src/app/services/actions/agent-inventory-navigation.service';
import { CommonActionsService } from 'src/app/services/actions/common/common-actions.service';
import { RequiredParamsForChatGptEnterpriseDeleteConversationAction } from 'src/app/services/httpServices/actionsHttpService/actions-http-service.service';
import { MenuAction } from 'src/app/models/menuAction.model';
import { UnifiedPipelineService } from 'src/app/services/infra/unifiedPipeline/unified-pipeline.service';
import { StatusLabelEnum } from 'src/app/components/status-label/status-label.component';

@Component({
  selector: 'app-finding',
  templateUrl: './enterprise-ai-finding.component.html',
  styleUrls: ['./enterprise-ai-finding.component.scss'],
})
export class EnterpriseAiFindingComponent implements OnInit {
  integration = input.required<string>();
  finding = input.required<string>();
  viewModel = input<AiFindingsTableRowViewModel | null>(null);

  public isLoading$ = new BehaviorSubject<boolean>(true);
  public findingModel: FindingModel;
  public integrationName: string;
  public showNoData = false;
  public tabs: ISidepanelTab[];
  public activeTabIndex = 0;
  public findingDetailsCardFields: ICardField[];
  public isUpdatingLabels$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public actorCardFields: ICardField[];
  public relevantStepsCardFieldList: ICardField[][];
  public owaspRiskTag: ISecurityTag[] = [];
  public owaspAsiRiskTag: ISecurityTag[] = [];
  public owaspNhiRiskTag: ISecurityTag[] = [];
  public attackRiskTag: ISecurityTag[] = [];
  public triageSuggestion: ITimelineItem[] = [];
  public fixSuggestion: ITimelineItem[] = [];

  public customAgentTemplate = viewChild('customAgentTemplate', { read: TemplateRef });
  public menuActions: MenuAction[] = [];
  public isUnifiedPipelineEnabled = false;
  public statusLabel: WritableSignal<StatusLabelEnum> = signal(StatusLabelEnum.detected);
  public isTaintAnalysis = false;
  public taintAnalysisEvidence: Evidence[] = [];

  constructor(
    private readonly _storeService: StoreService,
    private readonly _aiFindingsService: AiFindingsHttpService,
    private readonly _aiAgentStepsService: AiAgentStepsHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _beautifyDisplayStringPipe: BeautifyDisplayStringPipe,
    private readonly _sidePanel: SidePanelService,
    private readonly _router: Router,
    private readonly _destroyRef: DestroyRef,
    private readonly _notificationService: NotificationsService,
    private readonly _matDialog: MatDialog,
    private readonly _featureFlagsService: FeatureFlagsService,
    private readonly _agentInventoryNavigationService: AgentInventoryNavigationService,
    private readonly _commonActionsService: CommonActionsService,
    private readonly _unifiedPipelineService: UnifiedPipelineService,
    private readonly _logger: LoggerService,
    private readonly _httpClient: HttpClient
  ) {
    this.isUnifiedPipelineEnabled = this._unifiedPipelineService.isEnabled();
  }

  public ngOnInit() {
    const storedFinding$ = this._storeService.getSelectedFinding$().pipe(take(1));
    const finding$ = storedFinding$.pipe(
      switchMap((storedFinding) => {
        if (storedFinding && storedFinding.id === this.finding()) {
          return of(storedFinding);
        }

        const viewModelFinding = this.viewModel()?.finding ?? null;
        if (viewModelFinding) {
          return of(viewModelFinding);
        }
        return this._aiFindingsService
          .getAiFinding(this.integration(), this.finding())
          .pipe(catchError(() => this._fetchMockFinding(this.finding())));
      })
    );

    combineLatest([finding$, this._storeService.integrations$])
      .pipe(
        take(1),
        filter(([finding]) => {
          if (!finding) {
            console.warn('Finding not found in the server. Not showing any data');
            this.showNoData = true;
            return false;
          }
          return true;
        }),
        tap(([finding]) => {
          this._storeService.setSelectedFinding(finding);
        }),
        finalize(() => this.isLoading$.next(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: ([finding, integrations]) => {
          this.initialize(finding, integrations);
        },
        error: (error) => {
          if (error.status === 404) this.showNoData = true;
          else this._httpErrorHandlerService.handle(error);
        },
      });
  }

  public onTabSelect(index: number) {
    this.activeTabIndex = index;
  }

  public openStepDetails(step: AgentStepModel) {
    this._sidePanel.openAgentStepDetailsFromFinding(step.thread.id, step.id, step);
  }

  public navigateToThreadFindings() {
    // Open findings thread as nested panel
    const sourcePipeline = this.findingModel.sourcePipeline === 'inline' ? 'inline' : undefined;
    this._sidePanel.openFindingThreadNested(this.findingModel.thread.id, sourcePipeline);
  }

  private async initialize(finding: FindingModel, integrations: IntegrationModel[]): Promise<void> {
    if (
      finding.id?.startsWith('rsa-') ||
      finding.id?.startsWith('finding-') ||
      finding.id?.startsWith('spaiware-') ||
      finding.id?.startsWith('cursor-mcp-')
    ) {
      this._ensureRsaMockLabelsInStore();
    }

    const selectedIntegration: IntegrationModel | undefined = integrations.find(
      (integration) => integration.id === this.integration()
    );
    this.integrationName = selectedIntegration?.name ?? '';

    this.findingModel = finding;
    this.showNoData = false;
    this.menuActions = [];
    this.tabs = [{ labelPath: 'inventoryResourcePage.overviewTabTitle' }];
    this.statusLabel.set(finding.action === 'block' ? StatusLabelEnum.blocked : StatusLabelEnum.detected);
    this.isTaintAnalysis = isTaintAnalysisRule(finding.rule?.displayName);
    this.taintAnalysisEvidence = this.isTaintAnalysis ? this._extractTaintAnalysisEvidence(finding) : [];

    this.findingDetailsCardFields = this.isTaintAnalysis
      ? this._getTaintAnalysisSummaryFields()
      : this.getFindingDetailsFields();

    if (!this.isTaintAnalysis) {
      this.relevantStepsCardFieldList = this.getRelevantStepsFields();
      this.actorCardFields = this.getActorFields();

      this.owaspRiskTag = this.__mapOwaspToSecurityTags(
        this.findingModel.rule.owasp.filter((owasp) => owasp.startsWith('LLM-') || owasp.startsWith('LCNC-'))
      );

      this.owaspAsiRiskTag = this.__mapOwaspToSecurityTags(
        this.findingModel.rule.owasp.filter((owasp) => owasp.startsWith('ASI-'))
      );

      const nhiDisplayMap: Record<string, string> = {
        'NHI1-2025': 'Improper Offboarding',
        'NHI2-2025': 'Secret Leakage',
        'NHI3-2025': 'Vulnerable Third Party NHI',
        'NHI4-2025': 'Insecure Authentication',
        'NHI5-2025': 'Overprivileged NHI',
        'NHI6-2025': 'Insecure Cloud Deployment',
        'NHI7-2025': 'Long Lived Secrets',
        'NHI8-2025': 'Environment Isolation',
        'NHI9-2025': 'NHI Reuse',
        'NHI10-2025': 'Human Use Of NHI',
      };
      this.owaspNhiRiskTag = this.findingModel.rule.owasp
        .filter((owasp) => owasp.startsWith('NHI'))
        .map((nhi) => ({
          key: 'OWASP NHI',
          value: nhiDisplayMap[nhi] ?? nhi,
        }));

      this.attackRiskTag = this.findingModel.rule.genAiAttack.map((attack) => ({
        key: this._labelsProviderPipe.transform('violationPage.riskReasonCardAttackFieldKey'),
        value: this._beautifyDisplayStringPipe.transform(findingAttackToDisplayName[attack.toUpperCase()]),
      }));

      this.findingModel.rule.triageSuggestion
        .split(/\r?\n/)
        .forEach((element) => this.triageSuggestion.push({ text: element }));
      this.findingModel.rule.fixSuggestion
        .split(/\r?\n/)
        .forEach((element) => this.fixSuggestion.push({ text: element }));

      this._initializeMenuActions();
    }
  }

  private _initializeMenuActions(): void {
    const relatedStep = this.findingModel.relatedSteps[0];
    if (relatedStep?.service === AiService.chatGptEnterprise) {
      this.menuActions = [
        {
          name: 'deleteThread',
          displayNameLabel: 'commonActions.chatGptEnterpriseDeleteConversationAction',
          disabled: false,
          iconPath: 'assets/images/common/delete.svg',
        },
      ];
    } else {
      this.menuActions = [];
    }
  }

  public onActionSelected(actionName: string): void {
    if (actionName === 'deleteThread') {
      this._deleteThread();
    }
  }

  private _deleteThread(): void {
    const relatedStep = this.findingModel?.relatedSteps[0];
    if (!relatedStep) return;

    const params = {
      integrationId: this.integration(),
      stepId: relatedStep.id,
    } as RequiredParamsForChatGptEnterpriseDeleteConversationAction;

    this._commonActionsService
      .chatGptEnterpriseDeleteGptConversationAction(params)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe();
  }

  private getFindingDetailsFields(): ICardField[] {
    const relatedStep = this.findingModel.relatedSteps?.[0];

    const fieldsToReturn: ICardField[] = [
      {
        key: this._labelsProviderPipe.transform('aiFindingPage.ruleIdLabel'),
        value: this.findingModel.rule.id,
        copyableText: this.findingModel.rule.id,
        isCopyable: true,
      },
      {
        key: this._labelsProviderPipe.transform('aiFindingPage.threadIdLabel'),
        value: this.findingModel.thread.id,
        copyableText: this.findingModel.thread.id,
        isCopyable: true,
      },
      {
        key: this._labelsProviderPipe.transform('aiFindingPage.aiServiceLabel'),
        value: this._displayCamelCaseStringPipe.transform(this.findingModel.rule.service),
        iconPath: getAiServiceLogoPath(this.findingModel.rule.service),
      },
    ];

    if (relatedStep) {
      const isCustomM365Agent = isM365CustomAgent(relatedStep.service, relatedStep.botDetails.displayName);

      fieldsToReturn.push(this._buildClientApplicationField(relatedStep.clientContext.clientApplication));

      const agentNameField: ICardField = {
        key: this._labelsProviderPipe.transform('aiFindingPage.agentNameLabel'),
      };

      if (isCustomM365Agent) {
        agentNameField.customTemplate = this.customAgentTemplate();
      } else {
        agentNameField.value = relatedStep.botDetails.displayName;
        agentNameField.iconPath = getAiServiceLogoPath(relatedStep.service);

        if (
          this._agentInventoryNavigationService.shouldShowAgentLink({
            inventoryId: relatedStep.botDetails?.inventoryId,
            resourceId: relatedStep.botDetails?.platformInfo?.resourceId,
            service: relatedStep.service,
          })
        ) {
          agentNameField.method = () => {
            this._agentInventoryNavigationService.navigateToAgent({
              inventoryId: relatedStep.botDetails?.inventoryId,
              resourceId: relatedStep.botDetails?.platformInfo?.resourceId,
              service: relatedStep.service,
              integration: this.integration(),
            });
          };
        }
      }

      fieldsToReturn.push(agentNameField);

      if (relatedStep.environment) {
        fieldsToReturn.push({
          key: this._labelsProviderPipe.transform('aiAgentStepPage.environmentNameFieldName'),
          value: relatedStep.environment.displayName,
          copyableText: relatedStep.environment.displayName,
          isCopyable: true,
        });
      }
    }

    return fieldsToReturn;
  }

  private _getTaintAnalysisSummaryFields(): ICardField[] {
    const relatedStep = this.findingModel.relatedSteps?.[0];

    const fields: ICardField[] = [
      {
        key: this._labelsProviderPipe.transform('aiFindingPage.ruleIdLabel'),
        value: this.findingModel.rule.id,
        copyableText: this.findingModel.rule.id,
        isCopyable: true,
      },
      {
        key: this._labelsProviderPipe.transform('aiFindingPage.threadIdLabel'),
        value: this.findingModel.thread.id,
        copyableText: this.findingModel.thread.id,
        isCopyable: true,
      },
    ];

    const agentResourceId = relatedStep?.botDetails?.platformInfo?.resourceId;
    if (agentResourceId) {
      fields.push({
        key: this._labelsProviderPipe.transform('aiInLineControlPage.agentIdLabel'),
        value: '',
        copyableText: agentResourceId,
        isCopyable: true,
        hideValue: true,
      });
    }

    fields.push({
      key: this._labelsProviderPipe.transform('aiFindingPage.aiServiceLabel'),
      value: relatedStep ? getAiServiceDisplayName(relatedStep.service) : 'N/A',
      iconPath: relatedStep ? getAiServiceLogoPath(relatedStep.service) : '',
    });

    const clientApp = relatedStep?.clientContext?.clientApplication;
    if (clientApp) {
      fields.push(this._buildClientApplicationField(clientApp));
    }

    if (relatedStep?.botDetails?.displayName) {
      fields.push({
        key: this._labelsProviderPipe.transform('aiFindingPage.agentNameLabel'),
        value: relatedStep.botDetails.displayName,
        iconPath: getAiServiceLogoPath(relatedStep.service),
      });
    }

    fields.push({
      key: this._labelsProviderPipe.transform('aiInLineControlPage.preventionTypeLabel'),
      value:
        this.findingModel.action === 'block'
          ? this._labelsProviderPipe.transform('aiInLineControlPage.preventionTypeBlocked')
          : this._labelsProviderPipe.transform('aiInLineControlPage.preventionTypeDetected'),
      iconPath:
        this.findingModel.action === 'block'
          ? 'assets/images/dataflow-icons/blocked.svg'
          : 'assets/images/dataflow-icons/shield-alert.svg',
    });

    return fields;
  }

  private _extractTaintAnalysisEvidence(finding: FindingModel): Evidence[] {
    const raw = finding.evidence?.[0]?.additionalData;
    const parsed = parseAdditionalData(raw);
    if (!parsed) {
      if (raw) {
        this._logger.warn('Taint analysis finding has additionalData but parsing failed', { findingId: finding.id });
      }
      return [];
    }

    let nestedEvidence: unknown = parsed['evidence'];

    // The backend may send evidence as a JSON string inside the additionalData object
    if (typeof nestedEvidence === 'string' && nestedEvidence.trimStart().startsWith('[')) {
      try {
        nestedEvidence = JSON.parse(nestedEvidence);
      } catch (error) {
        this._logger.warn('Taint analysis finding has malformed nested evidence JSON', {
          findingId: finding.id,
          error,
        });
        return [];
      }
    }

    if (!Array.isArray(nestedEvidence)) return [];

    return nestedEvidence.filter(
      (item): item is Evidence =>
        item != null &&
        typeof item === 'object' &&
        typeof item['title'] === 'string' &&
        typeof item['coreEvidence'] === 'string'
    );
  }

  private _buildClientApplicationField(clientApplication: string): ICardField {
    return {
      key: this._labelsProviderPipe.transform('aiFindingPage.clientApplicationLabel'),
      value: this._displayCamelCaseStringPipe.transform(clientApplication),
      iconPath: getClientApplicationIconPath(clientApplication),
    };
  }

  private getRelevantStepsFields(): ICardField[][] {
    return this.findingModel.relatedSteps.map((step) => {
      const fields: ICardField[] = [];
      fields.push({
        key: this._labelsProviderPipe.transform('aiFindingPage.stepTypeLabel'),
        value: this._displayCamelCaseStringPipe.transform(step.type),
      });
      fields.push({
        key: this._labelsProviderPipe.transform('aiFindingPage.timestampFieldName'),
        value: formatFromDateWithSeconds(step.timestamp),
      });
      fields.push({
        key: this._labelsProviderPipe.transform('aiFindingPage.stepDetailsLabel'),
        value: this._labelsProviderPipe.transform('aiFindingPage.linkToStepDetails'),
        method: () => this.openStepDetails(step),
      });
      if (step.clientContext.region)
        fields.push({
          key: this._labelsProviderPipe.transform('aiFindingPage.regionLabel'),
          value: step.clientContext.region,
        });
      return fields.concat(this._getSpecificStepFields(step));
    });
  }

  private getActorFields(): ICardField[] {
    const actorNameField: ICardField = {
      key: this._labelsProviderPipe.transform('aiFindingPage.nameLabel'),
      value: this.findingModel.mainActor.displayName,
      isCopyable: true,
    };

    const inventoryId = this.findingModel.mainActor?.inventoryId;
    if (this._isInventoryEnrichmentEnabled() && inventoryId != null && inventoryId.trim() !== '') {
      actorNameField.method = () => {
        this._sidePanel.openResourceDetailsFromIssue(inventoryId, this.integration());
      };
    }

    return [
      actorNameField,
      {
        key: this._labelsProviderPipe.transform('aiFindingPage.emailLabel'),
        value: this.findingModel.mainActor.email ?? 'N/A',
        isCopyable: true,
      },
    ];
  }

  private _fetchMockFinding(findingId: string): Observable<FindingModel> {
    if (
      findingId?.startsWith('rsa-') &&
      this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.RsaDemoRuntimeBoundaries)
    ) {
      return this._loadMockFindingFromFile(findingId, 'assets/mockData/rsaFindingsMockData.json');
    }

    if (
      (findingId?.startsWith('finding-') || findingId?.startsWith('spaiware-') || findingId?.startsWith('cursor-mcp-')) &&
      this._featureFlagsService.isFeatureEnabled(
        FeatureFlagTypes.SpmIamDemoAmex
      )
    ) {
      return this._loadMockFindingFromFile(findingId, 'assets/mockData/iamDemoFindingsMockData.json');
    }

    return throwError(() => new Error('Not found'));
  }

  private _loadMockFindingFromFile(findingId: string, path: string): Observable<FindingModel> {
    return this._httpClient.get<Record<string, FindingModel>>(path).pipe(
      map((data) => {
        const finding = data[findingId];
        if (!finding) {
          throw new Error('Mock finding not found');
        }
        return finding;
      })
    );
  }

  private _ensureRsaMockLabelsInStore(): void {
    this._storeService.labels$.pipe(take(1)).subscribe((labels) => {
      const mockLabels: LabelModel[] = [
        { id: 'runtime', displayName: 'Runtime', color: '#e74c3c' },
        { id: 'buildtime', displayName: 'Buildtime', color: '#f39c12' },
        { id: 'nhi5-2025', displayName: 'NHI5:2025 - Overprivileged NHI', color: '#8e44ad' },
      ];
      const missing = mockLabels.filter((mock) => !labels.some((l) => l.id === mock.id));
      if (missing.length > 0) {
        this._storeService.labels$.next([...labels, ...missing]);
      }
    });
  }

  private _isInventoryEnrichmentEnabled(): boolean {
    return this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.DrInventoryEnrichment);
  }

  public getAiServiceIconPath(): string {
    if (!this.findingModel) return '';
    const step = this.findingModel.relatedSteps?.[0];
    return step ? getAiServiceLogoPath(step.service) : getAiServiceLogoPath(this.findingModel.rule.service);
  }

  public hasInventoryId(): boolean {
    if (!this._isInventoryEnrichmentEnabled()) {
      return false;
    }
    const inventoryId = this.findingModel?.relatedSteps[0]?.botDetails?.inventoryId;
    return inventoryId != null && inventoryId.trim() !== '';
  }

  public navigateToAgentFromCustomTemplate(): void {
    const relatedStep = this.findingModel?.relatedSteps[0];
    if (!relatedStep) return;

    const inventoryId = relatedStep.botDetails?.inventoryId;
    if (inventoryId != null && inventoryId.trim() !== '') {
      this._sidePanel.openResourceDetailsFromIssue(inventoryId, this.integration());
    }
  }

  private _isMockFinding(): boolean {
    return (
      this.findingModel?.id?.startsWith('rsa-') ||
      this.findingModel?.id?.startsWith('finding-') ||
      this.findingModel?.id?.startsWith('spaiware-') ||
      this.findingModel?.id?.startsWith('cursor-mcp-')
    );
  }

  private _getMockSensitiveData(stepId: string): Observable<ActionGetAgentStepSensitiveDataResponseModel> {
    type ContentMap = Record<string, ActionGetAgentStepSensitiveDataResponseModel>;
    const notFound = {
      resultCode: 'error',
      title: 'Not Found',
      transcript: '',
    } as ActionGetAgentStepSensitiveDataResponseModel;

    const rsa$ = this._httpClient
      .get<ContentMap>('assets/mockData/rsaFetchContentMockData.json')
      .pipe(catchError(() => of({} as ContentMap)));

    const iam$ = this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.SpmIamDemoAmex)
      ? this._httpClient
          .get<ContentMap>('assets/mockData/iamDemoFetchContentMockData.json')
          .pipe(catchError(() => of({} as ContentMap)))
      : of({} as ContentMap);

    return combineLatest([rsa$, iam$]).pipe(
      map(([rsaData, iamData]) => iamData[stepId] ?? rsaData[stepId] ?? notFound)
    );
  }

  private _getSensitiveDataMethodForStep(
    step: AgentStepModel,
    action: SensitiveDataRequestActions
  ): () => Observable<ActionGetAgentStepSensitiveDataResponseModel> {
    if (this._isMockFinding()) {
      return () => this._getMockSensitiveData(step.id);
    }
    return () => this._aiAgentStepsService.getAgentStepSensitiveData(this.integration(), step.id, action);
  }

  private _getSpecificStepFields(step: AgentStepModel): ICardField[] {
    const fields: ICardField[] = [];
    switch (step.type) {
      case StepType.toolRequestStep:
        fields.push({
          key: this._labelsProviderPipe.transform('aiAgentStepPage.messageFieldName'),
          value: '',
          isRawValue: true,
          sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.showPromptContentLinkName'),
          showPopupBtn: true,
          sensitiveDataMethod: this._getSensitiveDataMethodForStep(step, SensitiveDataRequestActions.invocationParams),
        });
        break;
      case StepType.toolResponseStep:
        fields.push({
          key: this._labelsProviderPipe.transform('aiAgentStepPage.messageFieldName'),
          value: '',
          isRawValue: true,
          sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.showPromptContentLinkName'),
          showPopupBtn: true,
          sensitiveDataMethod: this._getSensitiveDataMethodForStep(step, SensitiveDataRequestActions.invocationResult),
        });
        break;
      case StepType.aiMessageStep:
      case StepType.userMessageStep:
        fields.push({
          key: this._labelsProviderPipe.transform('aiAgentStepPage.messageFieldName'),
          value: '',
          isRawValue: true,
          sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.showPromptContentLinkName'),
          showPopupBtn: true,
          sensitiveDataMethod: this._getSensitiveDataMethodForStep(step, SensitiveDataRequestActions.transcript),
        });
        break;
      case StepType.ragStep:
        // Add RAG Query fetch functionality for all services
        const shouldShowRagQuery =
          step.ragQueryAvailable !== undefined ? step.ragQueryAvailable === true : step.ragQuery != null;

        if (shouldShowRagQuery) {
          fields.push({
            key: this._labelsProviderPipe.transform('aiAgentStepPage.ragQuery'),
            value: '',
            isRawValue: true,
            sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.ragQuery'),
            showPopupBtn: true,
            sensitiveDataMethod: this._getSensitiveDataMethodForStep(step, SensitiveDataRequestActions.ragQuery),
          });
        }

        switch (step.service) {
          case AiService.m365Copilot:
            if (step.sensitivityLabelId)
              fields.push({
                key: this._labelsProviderPipe.transform('aiAgentStepPage.sensitivityLabelFieldName'),
                value: step.sensitivityLabelId,
                isCopyable: true,
                copyableText: step.sensitivityLabelId,
              });
            break;
        }
        break;
    }

    return fields;
  }

  public removeLabelBindingFromFinding(labelBinding: LabelBindingModel): void {
    this._aiFindingsService
      .unbindLabelFromFinding(this.finding(), this.integration(), labelBinding)
      .pipe(
        concatMap(() => {
          const sp = this.findingModel.sourcePipeline === 'inline' ? 'inline' : undefined;
          return this._aiFindingsService.getAiFinding(this.integration(), this.finding(), sp);
        }),
        tap(() => this._notificationService.success('Label removed')),
        map((finding: FindingModel) => {
          this.findingModel = finding;
          this._storeService.setSelectedFinding(finding);
          return finding;
        }),
        concatMap((updatedFinding) => {
          return this._storeService.updateFindingsPageData(
            () => this._storeService.getFindingsPageData$(),
            (data) => this._storeService.setFindingsPageData(data),
            updatedFinding
          );
        }),
        catchError((error) => {
          this._notificationService.error(`Could not remove label: ${labelBinding.label.displayName}.`);
          return throwError(() => error);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  public addLabelsClicked() {
    this._matDialog
      .open(LabelsDialogComponent, {
        data: {
          existingSelectedLabels: this.findingModel.labelBindings || [],
          titleLabelPath: 'labelsDialog.setLabelsTitle',
        } as LabelsDialogInputData,
        disableClose: false,
        autoFocus: false,
        panelClass: 'sidepanel-dialog-container',
        width: '350px',
      })
      .afterClosed()
      .pipe(
        filter((output: LabelsDialogOutputData | undefined): output is LabelsDialogOutputData => !!output),
        switchMap((output: LabelsDialogOutputData) => {
          const labelsToBind = output.labelsToBind.filter(
            (labelToBind: LabelModel) =>
              !this.findingModel.labelBindings?.some((label: LabelBindingModel) => label.label.id === labelToBind.id)
          );
          const labelsToUnbind =
            this.findingModel.labelBindings?.filter(
              (label: LabelBindingModel) =>
                !output.labelsToBind.some((labelToBind: LabelModel) => labelToBind.id === label.label.id)
            ) || [];

          this.isUpdatingLabels$.next(true);

          const bindLabels$: Observable<null | LabelBindingModel[]> =
            labelsToBind.length > 0
              ? forkJoin(
                  labelsToBind.map((label: LabelModel) =>
                    this._aiFindingsService
                      .bindLabelToFinding(this.finding(), this.integration(), label.id, output.note)
                      .pipe(
                        catchError((error) => {
                          this._notificationService.error(
                            `Could not bind some of the following labels: ${output.labelsToBind.map((l) => l.displayName).join(', ')}.`
                          );
                          return throwError(() => error);
                        })
                      )
                  )
                )
              : of(null);

          const unbindLabels$: Observable<null | void[]> =
            labelsToUnbind && labelsToUnbind?.length > 0
              ? forkJoin(
                  labelsToUnbind.map((label: LabelBindingModel) =>
                    this._aiFindingsService.unbindLabelFromFinding(this.finding(), this.integration(), label).pipe(
                      catchError((error) => {
                        this._notificationService.error(
                          `Could not unbind following label: ${label.label.displayName}.`
                        );
                        return throwError(() => error);
                      })
                    )
                  )
                )
              : of(null);

          return bindLabels$.pipe(
            concatMap(() => unbindLabels$),
            concatMap(() => {
              const sp = this.findingModel.sourcePipeline === 'inline' ? 'inline' : undefined;
              return this._aiFindingsService.getAiFinding(this.integration(), this.finding(), sp);
            })
          );
        }),
        tap((updatedFinding: FindingModel) => {
          this.findingModel = updatedFinding;
          this._storeService.setSelectedFinding(updatedFinding);
          this._notificationService.success('Labels updated');
        }),
        concatMap((updatedFinding) => {
          return this._storeService.updateFindingsPageData(
            () => this._storeService.getFindingsPageData$(),
            (data) => this._storeService.setFindingsPageData(data),
            updatedFinding
          );
        }),
        finalize(() => {
          this.isUpdatingLabels$.next(false);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private __mapOwaspToSecurityTags(owaspList: string[]): ISecurityTag[] {
    return owaspList.map((owasp) => ({
      key: this._labelsProviderPipe.transform(getviolationRuleRiskReasonCardFieldKey(owasp)),
      value: this._beautifyDisplayStringPipe.transform(anyOwaspToDisplayName[owasp]),
      link: getViolationRuleToOwaspLinks(owasp),
    }));
  }
}
